/* eslint-disable */
import { getData, postData, putData, deleteData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const token = localStorage.getItem("token");

// ============================================================
// 📋 BILLABLE ACTIVITIES
// ============================================================

/**
 * Get all billable activities
 * @param {Object} filters - Query filters (patient_id, doctor_id, company_id, etc.)
 * @returns {Promise<Object>} Response with activities list
 */
export const getAllBillableActivities = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_ALL_BILLABLE_ACTIVITIES + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getAllBillableActivities:", response);
    return response;
  } catch (error) {
    console.log("Error in getAllBillableActivities:", error.message);
    return null;
  }
};

/**
 * Get activity by ID
 * @param {string} activityId - Activity ID
 * @returns {Promise<Object>} Response with activity details
 */
export const getBillableActivityById = async (activityId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_BILLABLE_ACTIVITY_BY_ID + 
      `/${activityId}?token=${token}&include_sales_orders=true`;  // ✅ AGREGAR PARÁMETRO
    
    const response = await getData(requestUrl);
    console.log("Response from getBillableActivityById:", response);
    return response;
  } catch (error) {
    console.log("Error in getBillableActivityById:", error.message);
    return null;
  }
};

/**
 * Create new billable activity
 * @param {Object} activityData - Activity data
 * @returns {Promise<Object>} Response with created activity
 */
export const createBillableActivity = async (activityData) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_BILLABLE_ACTIVITY + 
      `?token=${token}`;
    
    const response = await postData(requestUrl, activityData);
    console.log("Response from createBillableActivity:", response);
    return response;
  } catch (error) {
    console.log("Error in createBillableActivity:", error.message);
    return null;
  }
};

/**
 * Update billable activity
 * @param {string} activityId - Activity ID
 * @param {Object} activityData - Updated activity data
 * @returns {Promise<Object>} Response with updated activity
 */
export const updateBillableActivity = async (activityId, activityData) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_UPDATE_BILLABLE_ACTIVITY + 
      `/${activityId}?token=${token}`;
    
    const response = await putData(requestUrl, activityData);
    console.log("Response from updateBillableActivity:", response);
    return response;
  } catch (error) {
    console.log("Error in updateBillableActivity:", error.message);
    return null;
  }
};

/**
 * Update activity status with history
 * @param {string} activityId - Activity ID
 * @param {Object} statusData - { newStatus, reason, metadata }
 * @returns {Promise<Object>} Response with updated activity
 */
export const updateActivityStatus = async (activityId, statusData) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_UPDATE_ACTIVITY_STATUS + 
      `/${activityId}/status?token=${token}`;
    
    const response = await putData(requestUrl, statusData);
    console.log("Response from updateActivityStatus:", response);
    return response;
  } catch (error) {
    console.log("Error in updateActivityStatus:", error.message);
    return null;
  }
};

/**
 * Update billing status (legacy)
 * @param {string} activityId - Activity ID
 * @param {Object} billingData - { billing_status, order_id, invoice_id }
 * @returns {Promise<Object>} Response with updated activity
 */
export const updateBillingStatus = async (activityId, billingData) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_UPDATE_BILLING_STATUS + 
      `/${activityId}/billing-status?token=${token}`;
    
    const response = await putData(requestUrl, billingData);
    console.log("Response from updateBillingStatus:", response);
    return response;
  } catch (error) {
    console.log("Error in updateBillingStatus:", error.message);
    return null;
  }
};

/**
 * Get pending activities
 * @param {Object} filters - Query filters (patient_id, doctor_id, company_id, etc.)
 * @returns {Promise<Object>} Response with pending activities
 */
export const getPendingActivities = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_PENDING_ACTIVITIES + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getPendingActivities:", response);
    return response;
  } catch (error) {
    console.log("Error in getPendingActivities:", error.message);
    return null;
  }
};

/**
 * Get activity statistics
 * @param {Object} filters - Query filters (patient_id, doctor_id, company_id)
 * @returns {Promise<Object>} Response with statistics
 */
export const getActivityStatistics = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_ACTIVITY_STATISTICS + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getActivityStatistics:", response);
    return response;
  } catch (error) {
    console.log("Error in getActivityStatistics:", error.message);
    return null;
  }
};

/**
 * Get activities grouped by criteria
 * @param {Object} filters - { groupBy: 'patient'|'doctor'|'company', patient_id, doctor_id, company_id }
 * @returns {Promise<Object>} Response with grouped activities
 */
export const getActivitiesGrouped = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_ACTIVITIES_GROUPED + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getActivitiesGrouped:", response);
    return response;
  } catch (error) {
    console.log("Error in getActivitiesGrouped:", error.message);
    return null;
  }
};

// ============================================================
// 📝 CREATE FROM SOURCE DOCUMENTS
// ============================================================

/**
 * Create from appointment
 * @param {string} appointmentId - Appointment ID
 * @param {Object} data - { customPrice, splitConfig }
 * @returns {Promise<Object>} Response
 */
export const createFromAppointment = async (appointmentId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_APPOINTMENT + 
      `/${appointmentId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createFromAppointment:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromAppointment:", error.message);
    return null;
  }
};

/**
 * Create from medical test
 * @param {string} testId - Medical test ID
 * @param {Object} data - { customPrice, splitConfig }
 * @returns {Promise<Object>} Response
 */
export const createFromMedicalTest = async (testId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_MEDICAL_TEST + 
      `/${testId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createFromMedicalTest:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromMedicalTest:", error.message);
    return null;
  }
};

/**
 * Create from diagnostic imaging
 * @param {string} imagingId - Diagnostic imaging ID
 * @param {Object} data - { customPrice, splitConfig }
 * @returns {Promise<Object>} Response
 */
export const createFromDiagnosticImaging = async (imagingId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_DIAGNOSTIC_IMAGING + 
      `/${imagingId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createFromDiagnosticImaging:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromDiagnosticImaging:", error.message);
    return null;
  }
};

/**
 * Create from immunization
 * @param {string} immunizationId - Immunization ID
 * @param {Object} data - { customPrice, splitConfig }
 * @returns {Promise<Object>} Response
 */
export const createFromImmunization = async (immunizationId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_IMMUNIZATION + 
      `/${immunizationId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createFromImmunization:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromImmunization:", error.message);
    return null;
  }
};

/**
 * Create from medicine
 * @param {string} medicineId - Medicine ID
 * @param {Object} data - { customPrice, splitConfig }
 * @returns {Promise<Object>} Response
 */
export const createFromMedicine = async (medicineId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_MEDICINE + 
      `/${medicineId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createFromMedicine:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromMedicine:", error.message);
    return null;
  }
};

/**
 * Create from prescription
 * @param {string} prescriptionId - Prescription ID
 * @param {Object} data - { customPrice, splitConfig }
 * @returns {Promise<Object>} Response
 */
export const createFromPrescription = async (prescriptionId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_PRESCRIPTION + 
      `/${prescriptionId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createFromPrescription:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromPrescription:", error.message);
    return null;
  }
};

// ============================================================
// 📊 POTENTIAL BILLABLE ACTIVITIES
// ============================================================

/**
 * Get all potential billable activities
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with potential activities
 */
export const getAllPotentialActivities = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_ALL_POTENTIAL_ACTIVITIES + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getAllPotentialActivities:", response);
    return response;
  } catch (error) {
    console.log("Error in getAllPotentialActivities:", error.message);
    return null;
  }
};

/**
 * Get potential activities grouped by patient
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with grouped activities
 */
export const getPotentialActivitiesGroupedByPatient = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_POTENTIAL_BY_PATIENT + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getPotentialActivitiesGroupedByPatient:", response);
    return response;
  } catch (error) {
    console.log("Error in getPotentialActivitiesGroupedByPatient:", error.message);
    return null;
  }
};

/**
 * Get billing status summary
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with billing status summary
 */
export const getBillingStatusSummary = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_BILLING_STATUS_SUMMARY + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getBillingStatusSummary:", response);
    return response;
  } catch (error) {
    console.log("Error in getBillingStatusSummary:", error.message);
    return null;
  }
};

/**
 * Get unbilled activities for patient
 * @param {string} patientId - Patient ID
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with unbilled activities
 */
export const getUnbilledActivitiesForPatient = async (patientId, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_UNBILLED_FOR_PATIENT + 
      `/${patientId}/unbilled?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getUnbilledActivitiesForPatient:", response);
    return response;
  } catch (error) {
    console.log("Error in getUnbilledActivitiesForPatient:", error.message);
    return null;
  }
};

/**
 * Check if activity is already billed
 * @param {string} sourceRefId - Source reference ID
 * @param {string} sourceRefType - Source reference type (e.g., 'appointment', 'medical_test')
 * @returns {Promise<Object>} Response
 */
export const checkActivityBilled = async (sourceRefId, sourceRefType) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CHECK_ACTIVITY_BILLED + 
      `/${sourceRefId}?sourceRefType=${sourceRefType}&token=${token}`;
    
    const response = await getData(requestUrl);
    console.log("Response from checkActivityBilled:", response);
    return response;
  } catch (error) {
    console.log("Error in checkActivityBilled:", error.message);
    return null;
  }
};

/**
 * Create billable activity from potential activity
 * @param {Object} potentialActivityData - Potential activity data
 * @param {number} customPrice - Optional custom price
 * @returns {Promise<Object>} Response
 */
export const createFromPotentialActivity = async (potentialActivityData, customPrice = null) => {
  try {
    const queryParams = customPrice ? `?customPrice=${customPrice}&token=${token}` : `?token=${token}`;
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_FROM_POTENTIAL + 
      queryParams;
    
    const response = await postData(requestUrl, potentialActivityData);
    console.log("Response from createFromPotentialActivity:", response);
    return response;
  } catch (error) {
    console.log("Error in createFromPotentialActivity:", error.message);
    return null;
  }
};

/**
 * Create bulk billable activities from potential activities
 * @param {Object} data - { activities: [{ activity_type, source_ref_id, ... }] }
 * @returns {Promise<Object>} Response
 */
export const createBulkFromPotentialActivities = async (data) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_BULK_FROM_POTENTIAL + 
      `?token=${token}`;
    
    console.log("🔍 Iniciando createBulkFromPotentialActivities");
    console.log("📦 Datos recibidos:", {
      activities_count: data.activities?.length,
      first_activity: data.activities?.[0]
    });

    // ✅ TRANSFORMACIÓN CON VALIDACIÓN ESTRICTA
    const payload = {
      activities: data.activities.map((activity, index) => {
        console.log(`🔍 Procesando actividad ${index + 1}:`, {
          description: activity.description,
          unit_price: activity.unit_price,
          payment_splits_count: activity.payment_splits?.length
        });

        // ✅ VALIDACIÓN DE PRECIO
        const unitPrice = parseFloat(activity.unit_price || activity.pricing?.unit_price || 0);
        if (isNaN(unitPrice) || unitPrice < 0) {
          throw new Error(`Actividad "${activity.description}" tiene precio inválido: ${activity.unit_price}`);
        }

        // ✅ VALIDACIÓN DE SPLITS
        const splits = activity.payment_splits || [];
        if (!Array.isArray(splits) || splits.length === 0) {
          throw new Error(`Actividad "${activity.description}" no tiene splits configurados`);
        }

        // ✅ NORMALIZAR SPLITS
        const normalizedSplits = splits.map((split, splitIndex) => {
          if (!split.payer_id) {
            throw new Error(`Split ${splitIndex + 1} sin payer_id en "${activity.description}"`);
          }
          
          return {
            payer_id: split.payer_id,
            payer_type: split.payer_type === 'User' ? 'Account' : split.payer_type,
            percent: parseFloat(split.percent || 0),
            max_amount: split.max_amount ? parseFloat(split.max_amount) : null,
            payer_snapshot: split.payer_snapshot || {
              name: 'Sin nombre',
              display_name: 'Sin nombre',
              type_description: split.payer_type === 'Company' ? 'Compañía' : 'Paciente',
              contact: {},
              address: {}
            }
          };
        });

        // ✅ CONSTRUCCIÓN DEL PAYLOAD CORREGIDO
        const activityPayload = {
          // Referencias
          activityType: activity.activityType || activity.source_ref_type || 'Agenda',
          activityId: activity.activityId || activity.source_id,
          
          // IDs (SIN guion bajo para que el backend los mapee correctamente)
          patientId: activity.patientId || activity.patient_id,
          doctorId: activity.doctorId || activity.doctor_id,
          companyId: activity.companyId || activity.company_id,
          
          // Descripción
          activity_type: activity.activity_type,
          description: activity.description || 'Sin descripción',
          service_date: activity.service_date,
          
          // Precios
          unit_price: unitPrice,
          quantity: parseInt(activity.quantity || activity.pricing?.qty || 1, 10),
          discount_amount: parseFloat(activity.discount_amount || activity.pricing?.discount || 0),
          tax_percentage: parseFloat(activity.tax_percentage || activity.pricing?.tax_rate || 0),
          
          // Pricing object
          pricing: {
            source: 'MANUAL',
            priced_at: new Date().toISOString(),
            currency: 'EUR',
            unit_price: unitPrice,
            qty: parseInt(activity.quantity || activity.pricing?.qty || 1, 10),
            discount: parseFloat(activity.discount_amount || activity.pricing?.discount || 0),
            tax_profile: activity.pricing?.tax_profile || (activity.tax_percentage > 0 ? 'IVA21' : 'EXENTO'),
            tax_rate: parseFloat(activity.tax_percentage || activity.pricing?.tax_rate || 0),
            components: [],
            notes: activity.notes || ''
          },
          
          // ✅ CRÍTICO: USAR payment_splits (NO "split")
          payment_splits: normalizedSplits,
          
          // Adicionales
          currency: 'EUR',
          status: '1',
          performed_at: activity.service_date
        };

        // ✅ LOG CORRECTO
        console.log(`✅ Actividad ${index + 1} preparada:`, {
          description: activityPayload.description,
          unit_price: activityPayload.unit_price,
          payment_splits_count: activityPayload.payment_splits.length,  // ✅ CORREGIDO
          patientId: activityPayload.patientId
        });

        return activityPayload;
      })
    };
    
    // ✅ VALIDACIÓN PRE-ENVÍO
    console.log("📦 Payload final construido:");
    console.log(`   - Total actividades: ${payload.activities.length}`);
    
    const invalidActivities = payload.activities.filter(act => {
      const hasValidPrice = typeof act.pricing.unit_price === 'number' && act.pricing.unit_price >= 0;
      const hasSplits = Array.isArray(act.payment_splits) && act.payment_splits.length > 0;  // ✅ CORREGIDO
      
      if (!hasValidPrice) {
        console.error(`❌ Precio inválido en "${act.description}":`, act.pricing.unit_price);
      }
      if (!hasSplits) {
        console.error(`❌ Splits inválidos en "${act.description}":`, act.payment_splits);
      }
      
      return !hasValidPrice || !hasSplits;
    });
    
    if (invalidActivities.length > 0) {
      console.error("❌ Actividades inválidas detectadas:", invalidActivities);
      throw new Error(`${invalidActivities.length} actividad(es) tienen datos inválidos`);
    }

    console.log("✅ Todas las validaciones pasadas");
    console.log("📦 Primera actividad (preview):", JSON.stringify(payload.activities[0], null, 2));
    
    // ✅ ENVÍO AL BACKEND
    console.log("🚀 Enviando petición al backend...");
    const response = await postData(requestUrl, payload);
    
    console.log("✅ Respuesta del servidor:", response);
    
    if (!response) {
      throw new Error('No se recibió respuesta del servidor');
    }
    
    if (!response.success && !response.status) {
      throw new Error(response.message || 'Error en la respuesta del servidor');
    }
    
    return response;
    
  } catch (error) {
    console.error("❌ Error en createBulkFromPotentialActivities:", error);
    console.error("❌ Stack completo:", error.stack);
    
    return {
      status: false,
      success: false,
      message: error.message || 'Error desconocido al crear actividades',
      error: error.toString()
    };
  }
};


// ============================================================
// 📄 SALES ORDERS
// ============================================================

/**
 * Create sales orders from activities (with splits support)
 * @param {Object} data - { activity_ids, grouping_mode, invoice_date, payer_override }
 * @returns {Promise<Object>} Response with created orders
 */
export const createSalesOrdersFromActivities = async (data) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_SALES_ORDERS_FROM_ACTIVITIES + 
      `?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createSalesOrdersFromActivities:", response);
    return response;
  } catch (error) {
    console.log("Error in createSalesOrdersFromActivities:", error.message);
    return null;
  }
};

/**
 * Get all sales orders
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with orders list
 */
export const getAllSalesOrders = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_ALL_SALES_ORDERS + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getAllSalesOrders:", response);
    return response;
  } catch (error) {
    console.log("Error in getAllSalesOrders:", error.message);
    return null;
  }
};

/**
 * Get sales order by ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Response with order details
 */
export const getSalesOrderById = async (orderId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_SALES_ORDER_BY_ID + 
      `/${orderId}?token=${token}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getSalesOrderById:", response);
    return response;
  } catch (error) {
    console.log("Error in getSalesOrderById:", error.message);
    return null;
  }
};

/**
 * Confirm sales order
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Response
 */
export const confirmSalesOrder = async (orderId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CONFIRM_SALES_ORDER + 
      `/${orderId}/confirm?token=${token}`;
    
    const response = await putData(requestUrl, {});
    console.log("Response from confirmSalesOrder:", response);
    return response;
  } catch (error) {
    console.log("Error in confirmSalesOrder:", error.message);
    return null;
  }
};

/**
 * Cancel sales order
 * @param {string} orderId - Order ID
 * @param {Object} data - { reason: string }
 * @returns {Promise<Object>} Response
 */
export const cancelSalesOrder = async (orderId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CANCEL_SALES_ORDER + 
      `/${orderId}?token=${token}`;
    
    const response = await deleteData(requestUrl, data);
    console.log("Response from cancelSalesOrder:", response);
    return response;
  } catch (error) {
    console.log("Error in cancelSalesOrder:", error.message);
    return null;
  }
};

/**
 * Get sales order details with complete payer information
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Response with complete order details
 */
export const getSalesOrderDetails = async (orderId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_SALES_ORDER_DETAILS + 
      `/${orderId}/details?token=${token}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getSalesOrderDetails:", response);
    return response;
  } catch (error) {
    console.log("Error in getSalesOrderDetails:", error.message);
    return null;
  }
};

/**
 * Get sales order statistics
 * @param {Object} filters - Query filters (patient_id, company_id)
 * @returns {Promise<Object>} Response with statistics
 */
export const getSalesOrderStatistics = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_SALES_ORDER_STATISTICS + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getSalesOrderStatistics:", response);
    return response;
  } catch (error) {
    console.log("Error in getSalesOrderStatistics:", error.message);
    return null;
  }
};

// ============================================================
// 🧾 INVOICES
// ============================================================

/**
 * Create invoice from sales order
 * @param {string} salesOrderId - Sales Order ID
 * @param {Object} data - { due_date: Date }
 * @returns {Promise<Object>} Response with created invoice
 */
export const createInvoiceFromSalesOrder = async (salesOrderId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CREATE_INVOICE_FROM_SALES_ORDER + 
      `/${salesOrderId}?token=${token}`;
    
    const response = await postData(requestUrl, data);
    console.log("Response from createInvoiceFromSalesOrder:", response);
    return response;
  } catch (error) {
    console.log("Error in createInvoiceFromSalesOrder:", error.message);
    return null;
  }
};

/**
 * Get all invoices
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with invoices list
 */
export const getAllInvoices = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_ALL_INVOICES + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getAllInvoices:", response);
    return response;
  } catch (error) {
    console.log("Error in getAllInvoices:", error.message);
    return null;
  }
};

/**
 * Get invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Response with invoice details
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_INVOICE_BY_ID + 
      `/${invoiceId}?token=${token}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getInvoiceById:", response);
    return response;
  } catch (error) {
    console.log("Error in getInvoiceById:", error.message);
    return null;
  }
};

/**
 * Get invoice statistics
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Response with statistics
 */
export const getInvoiceStatistics = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_INVOICE_STATISTICS + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getInvoiceStatistics:", response);
    return response;
  } catch (error) {
    console.log("Error in getInvoiceStatistics:", error.message);
    return null;
  }
};

/**
 * Check overdue invoices
 * @returns {Promise<Object>} Response
 */
export const checkOverdueInvoices = async () => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CHECK_OVERDUE_INVOICES + 
      `?token=${token}`;
    
    const response = await getData(requestUrl);
    console.log("Response from checkOverdueInvoices:", response);
    return response;
  } catch (error) {
    console.log("Error in checkOverdueInvoices:", error.message);
    return null;
  }
};

/**
 * Get invoices by date range
 * @param {Object} filters - { start_date, end_date, invoice_status }
 * @returns {Promise<Object>} Response with invoices
 */
export const getInvoicesByDateRange = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams({ token, ...filters }).toString();
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_GET_INVOICES_BY_DATE_RANGE + 
      `?${queryParams}`;
    
    const response = await getData(requestUrl);
    console.log("Response from getInvoicesByDateRange:", response);
    return response;
  } catch (error) {
    console.log("Error in getInvoicesByDateRange:", error.message);
    return null;
  }
};

/**
 * Mark invoice as sent
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Response
 */
export const markInvoiceAsSent = async (invoiceId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_MARK_INVOICE_AS_SENT + 
      `/${invoiceId}/mark-sent?token=${token}`;
    
    const response = await putData(requestUrl, {});
    console.log("Response from markInvoiceAsSent:", response);
    return response;
  } catch (error) {
    console.log("Error in markInvoiceAsSent:", error.message);
    return null;
  }
};

/**
 * Add payment to invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} paymentData - Payment information
 * @returns {Promise<Object>} Response
 */
export const addPaymentToInvoice = async (invoiceId, paymentData) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_ADD_PAYMENT_TO_INVOICE + 
      `/${invoiceId}/payment?token=${token}`;
    
    const response = await postData(requestUrl, paymentData);
    console.log("Response from addPaymentToInvoice:", response);
    return response;
  } catch (error) {
    console.log("Error in addPaymentToInvoice:", error.message);
    return null;
  }
};

/**
 * Cancel invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} data - { reason: string }
 * @returns {Promise<Object>} Response
 */
export const cancelInvoice = async (invoiceId, data = {}) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_CANCEL_INVOICE + 
      `/${invoiceId}?token=${token}`;
    
    const response = await deleteData(requestUrl, data);
    console.log("Response from cancelInvoice:", response);
    return response;
  } catch (error) {
    console.log("Error in cancelInvoice:", error.message);
    return null;
  }
};

/**
 * Sync invoice with Odoo
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Response
 */
export const syncInvoiceWithOdoo = async (invoiceId) => {
  try {
    let requestUrl = 
      url.API_BASE_URL + 
      url.API_SYNC_INVOICE_WITH_ODOO + 
      `/${invoiceId}/sync-odoo?token=${token}`;
    
    const response = await postData(requestUrl, {});
    console.log("Response from syncInvoiceWithOdoo:", response);
    return response;
  } catch (error) {
    console.log("Error in syncInvoiceWithOdoo:", error.message);
    return null;
  }
};